import type { SimInput } from '../shared/sim';

export interface StickState {
  active: boolean;
  /** Screen coords of the anchor and the current touch. */
  ax: number;
  ay: number;
  tx: number;
  ty: number;
  /** -1..1 */
  value: number;
}

const MAX_TRAVEL = 76;
const DEAD_ZONE = 7;
const TAP_MS = 190;
const TAP_SLOP = 14;
const FLICK_UP = 34;
const TUCK_DOWN = 30;

/**
 * Thumb-anchored stick on the left, two action buttons on the right.
 *
 * The stick anchors wherever the thumb lands rather than at a fixed spot, so
 * you can rest your hand anywhere and it still tracks. Lifting off recentres.
 */
export class InputController {
  readonly stick: StickState = { active: false, ax: 0, ay: 0, tx: 0, ty: 0, value: 0 };

  private el: HTMLElement | null = null;
  private stickPointer: number | null = null;
  private downAt = 0;
  private moved = 0;

  private hopLatchFrames = 0;
  private hopQueued = false;
  private stickTuck = false;
  private send = false;
  private trick = false;

  private keySteer = 0;
  private keys = new Set<string>();
  private enabled = false;

  /** Fraction of the viewport width reserved for the stick. */
  private stickZone = 0.62;

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.enabled) return;
    const rect = this.el!.getBoundingClientRect();
    const px = e.clientX - rect.left;
    if (px > rect.width * this.stickZone) return;
    if (this.stickPointer !== null) return;
    e.preventDefault();
    this.stickPointer = e.pointerId;
    this.stick.active = true;
    this.stick.ax = e.clientX;
    this.stick.ay = e.clientY;
    this.stick.tx = e.clientX;
    this.stick.ty = e.clientY;
    this.stick.value = 0;
    this.downAt = performance.now();
    this.moved = 0;
    this.el!.setPointerCapture?.(e.pointerId);
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (e.pointerId !== this.stickPointer) return;
    e.preventDefault();
    this.stick.tx = e.clientX;
    this.stick.ty = e.clientY;
    const dx = e.clientX - this.stick.ax;
    const dy = e.clientY - this.stick.ay;
    this.moved = Math.max(this.moved, Math.hypot(dx, dy));

    const mag = Math.abs(dx);
    this.stick.value =
      mag < DEAD_ZONE ? 0 : Math.sign(dx) * Math.min(1, (mag - DEAD_ZONE) / (MAX_TRAVEL - DEAD_ZONE));

    // Flick up to hop. Re-anchor vertically so a second flick works without lifting off.
    if (dy < -FLICK_UP) {
      this.pressHop();
      this.stick.ay = e.clientY + FLICK_UP * 0.5;
    }
    this.stickTuck = e.clientY - this.stick.ay > TUCK_DOWN;
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (e.pointerId !== this.stickPointer) return;
    e.preventDefault();
    const quick = performance.now() - this.downAt < TAP_MS && this.moved < TAP_SLOP;
    if (quick) this.pressHop();
    this.stickPointer = null;
    this.stick.active = false;
    this.stick.value = 0;
    this.stickTuck = false;
    this.el!.releasePointerCapture?.(e.pointerId);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return;
    this.keys.add(e.code);
    if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') {
      e.preventDefault();
      this.pressHop();
      this.trick = true;
    }
    if (e.code === 'KeyJ' || e.code === 'Enter') this.trick = true;
    if (e.code === 'KeyK' || e.code === 'ShiftLeft') this.send = true;
    this.syncKeys();
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code);
    if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW') this.trick = false;
    if (e.code === 'KeyJ' || e.code === 'Enter') this.trick = false;
    if (e.code === 'KeyK' || e.code === 'ShiftLeft') this.send = false;
    this.syncKeys();
  };

  private syncKeys(): void {
    const left = this.keys.has('ArrowLeft') || this.keys.has('KeyA');
    const right = this.keys.has('ArrowRight') || this.keys.has('KeyD');
    this.keySteer = (right ? 1 : 0) - (left ? 1 : 0);
  }

  attach(el: HTMLElement): void {
    this.el = el;
    el.addEventListener('pointerdown', this.onPointerDown, { passive: false });
    el.addEventListener('pointermove', this.onPointerMove, { passive: false });
    el.addEventListener('pointerup', this.onPointerUp, { passive: false });
    el.addEventListener('pointercancel', this.onPointerUp, { passive: false });
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  detach(): void {
    const el = this.el;
    if (el) {
      el.removeEventListener('pointerdown', this.onPointerDown);
      el.removeEventListener('pointermove', this.onPointerMove);
      el.removeEventListener('pointerup', this.onPointerUp);
      el.removeEventListener('pointercancel', this.onPointerUp);
    }
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.el = null;
  }

  setEnabled(v: boolean): void {
    this.enabled = v;
    if (!v) this.reset();
  }

  reset(): void {
    this.stickPointer = null;
    this.stick.active = false;
    this.stick.value = 0;
    this.hopLatchFrames = 0;
    this.hopQueued = false;
    this.stickTuck = false;
    this.send = false;
    this.trick = false;
    this.keySteer = 0;
    this.keys.clear();
  }

  /** Called by the action button and by flicks/taps. */
  pressHop(): void {
    // Queue rather than extend, so two quick hops always produce two edges.
    if (this.hopLatchFrames > 0) {
      this.hopQueued = true;
      return;
    }
    this.hopLatchFrames = 9;
  }

  setTrick(down: boolean): void {
    this.trick = down;
  }

  setSend(down: boolean): void {
    this.send = down;
  }

  /** Consume one frame of input. */
  read(): SimInput {
    const steer = this.stick.active ? this.stick.value : this.keySteer;
    let hop = this.hopLatchFrames > 0;
    if (this.hopLatchFrames > 0) {
      this.hopLatchFrames--;
      if (this.hopLatchFrames === 0 && this.hopQueued) {
        this.hopQueued = false;
        this.hopLatchFrames = 9;
        hop = false; // one clear frame so the next press is a fresh edge
      }
    }
    const keyTuck = this.keys.has('ArrowDown') || this.keys.has('KeyS');
    return {
      steer,
      hop,
      tuck: this.stickTuck || keyTuck,
      send: this.send,
      trick: this.trick,
    };
  }
}
